import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import Decimal from 'decimal.js'
import request from '@/modules/base/util/request'
import lang from '@/lang'
import CheckIcon from '@assets/images/icons/check.svg'
import BaseDrawer from '@/components/drawers/base-drawer'
import InvoiceInfo from '@/modules/invoice/components/list/components/invoice-info'
import { CancelButton } from '@/modules/base/components/buttons/index'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import { setSelectedBookings, resetState } from '@/redux/ducks/bookingSelection/list'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { RadioInput } from '@/modules/base/components/inputs'
import {
  urls,
} from '@/modules/banking/config'

import BaseDrawerPanelHeader from '@/components/drawers/base-drawer-panel-header/base-drawer-panel-header'
import BookingSelectionList from '@/modules/base/components/booking-selection-list/booking-selection-list'
import { LIST_TYPES } from '@/redux/ducks/lists'
import { fetchList } from '@/redux/ducks/lists/requests/bookingSelectionList'
import { assignmentTypes, bookingAssignmentsProceedingTypes, RECEIPT_PAYMENT_TYPES, transactionTypes } from '@/modules/base/util/constants'
import { toast } from '@/modules/base/components/toast/toastConfig'
import getStateAndOutstandingAmount from './_helpers/getStateAndOutstandingAmount'
import getStateAndOutstandingAmountReceipt from '@/modules/receipt/_helpers/getStateAndOutstandingAmount'
import OnlineBankingModal, { showBankAccountModal } from '@/modules/base/components/modal/online-banking-modal/online-banking-modal'
import { fetchBankAccountList, openBankAccountAddModal } from '@/redux/ducks/banking/bankAccountList'
import ExpenseInfo from '@/modules/expense/components/list/components/expense-info'
import RevenueInfo from '@/modules/revenue/list/components/revenue-info'
import { PAYMENT_TYPE_PAYMENT } from '@/modules/payment/constants'
import navigateToCurrentPage from '@/utils/navigateToCurrentPage'
import { useLocation, useNavigate } from 'react-router'

const { BOOKING_SELECTION_LIST_TYPE } = LIST_TYPES
const { addAssignmentsUrl } = urls

const {
  SURCHARGE,
  PARTIAL,
  DISCOUNT,
  BANK_CHARGE,
  BOOK_LATER,
} = bookingAssignmentsProceedingTypes

const { TRANSACTION_TYPE_REVENUE, TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_CREDIT_NOTE, TRANSACTION_TYPE_INVOICE, TRANSACTION_TYPE_MANUAL_CREDIT_NOTE } = transactionTypes

const getTitle = (type) => {
  switch (type) {
    case TRANSACTION_TYPE_EXPENSE:
      return lang.payments.assignmentDrawer.secondViewHeaderExpense
    case TRANSACTION_TYPE_REVENUE:
      return lang.payments.assignmentDrawer.secondViewHeaderRevenue
    case TRANSACTION_TYPE_CREDIT_NOTE:
      return lang.payments.assignmentDrawer.secondViewHeaderCreditNote
    case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE:
      return lang.payments.assignmentDrawer.secondViewHeaderManualCreditNote
    default:
      return lang.payments.assignmentDrawer.secondViewHeader
  }
}

const buildNormalizedTransaction = (transaction, type) => {
  const { priceTotal, totalGross, bookings, financeApiId, outstandingAmount } = transaction
  const result = {
    outstandingAmount,
    bookings,
    totalGross,
    financeApiId,
  }
  switch (type) {
    case TRANSACTION_TYPE_EXPENSE:
      result.outstandingAmount = new Decimal(outstandingAmount).times(-1).toDP(2).toNumber()
      result.totalGross = new Decimal(priceTotal).times(-1).toDP(2).toNumber()
      break
    case TRANSACTION_TYPE_REVENUE:
      result.totalGross = priceTotal
  }
  return result
}

const useStyles = makeStyles((theme) => ({
  secondViewWrapper: {
    height: '100%',
    width: 'auto',
    minWidth: 320,
  },
  secondViewDetail: {
    padding: 30,
  },
  drawerFooter: {
    '& > :first-child': {
      width: 150,
    },
    '& > *': {
      width: 215,
    },
  },
  drawerContainer: {
    maxWidth: 850,
    width: 850,
  },
  differenceWrapper: {
    transition: '.1s ease-out',
    flexGrow: 1,
    height: 0,
    '&.show': {
      height: 135,
    },
  },
  differenceInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 20,
    paddingTop: 60,
  },
  differenceTitle: {
    fontSize: theme.fontSizes.large,
    marginBottom: 10,
  },
  highlight: {
    color: theme.primary,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTop: `1px solid ${theme.borderColor}`,
    marginTop: 1,
    overflow: 'hidden',
  },
}))

const AssignmentDrawer = (props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    open,
    transaction: transactionProps,
    isOverDue,
    onClose,
    onConfirm,
    onAssign,
  } = props

  const type = [TRANSACTION_TYPE_REVENUE, TRANSACTION_TYPE_EXPENSE].includes(transactionProps.type)
    ? transactionProps.type
    : transactionProps.type.includes(TRANSACTION_TYPE_CREDIT_NOTE)
      ? TRANSACTION_TYPE_CREDIT_NOTE
      : transactionProps.type.includes(TRANSACTION_TYPE_MANUAL_CREDIT_NOTE)
        ? TRANSACTION_TYPE_MANUAL_CREDIT_NOTE
        : TRANSACTION_TYPE_INVOICE
  const fetchNegativeBookings = [TRANSACTION_TYPE_CREDIT_NOTE, TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_MANUAL_CREDIT_NOTE].includes(type)
  const transaction = buildNormalizedTransaction(transactionProps, type)

  const dispatch = useDispatch()
  const {
    list: {
      assignedAmount,
      selectedBookings,
    },
  } = useSelector(state => state.bookingSelection)

  const {
    bankAccountList: { bankAccounts },
  } = useSelector(state => state.banking)

  const classes = useStyles()

  const [openBankingModal, setOpenBankingModal] = useState(false)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [selectedProceeding, setSelectedProceeding] = useState(PAYMENT_TYPE_PAYMENT)

  useEffect(() => {
    if (bankAccounts.length === 0) {
      dispatch(fetchBankAccountList()).then((bankAccounts) => {
        if (showBankAccountModal(bankAccounts)) {
          setOpenBankingModal(true)
        } else {
          setOpenDrawer(open)
        }
      })
    }
    // Anything in here is fired on component mount.
    const relevantBookings = transaction?.bookings?.filter(booking => booking.isNew) || []
    dispatch(setSelectedBookings(relevantBookings))
    return () => {
      // Anything in here is fired on component unmount.
      dispatch(resetState())
    }
  }, [])

  useEffect(() => {
    if (bankAccounts.length > 0) {
      if (showBankAccountModal(bankAccounts)) {
        setOpenBankingModal(true)
      } else {
        setOpenDrawer(open)
      }
    }
  }, [open])

  const handleCancelClick = () => {
    onClose && onClose()
  }

  const handleClose = () => {
    setOpenDrawer(false)
    onClose && onClose()
  }

  const finishAssignment = () => {
    const { financeApiId } = transaction

    let newBooking = {}
    if (selectedBookings.length > 1) {
      return toast.showToast({
        type: 'error',
        message: lang.payments.assignmentDrawer.errorMessageMultipleBookings,
      })
    } else {
      const [booking] = selectedBookings
      const { bookingAmount } = booking

      const amount = (outstandingAmount < 0 && bookingAmount < outstandingAmount)
        || (outstandingAmount > 0 && bookingAmount > outstandingAmount)
        ? outstandingAmount
        : bookingAmount

      const assignments = [{
        assignmentType: [TRANSACTION_TYPE_REVENUE, TRANSACTION_TYPE_EXPENSE].includes(type) ? assignmentTypes.EXPENSE : assignmentTypes.INVOICE,
        amount,
        assignmentTypeId: financeApiId,
      }]

      const difference = new Decimal(outstandingAmount).minus(bookingAmount).toDP(2)

      if (!difference.isZero() && selectedProceeding) {
        const baseData = {
          amount: amount === bookingAmount ? difference.toDP(2).toNumber() : difference.times(-1).toDP(2).toNumber(),
        }

        switch (selectedProceeding) {
          case bookingAssignmentsProceedingTypes.SURCHARGE:
            assignments.push({ ...baseData, assignmentType: assignmentTypes.SURCHARGE, assignmentTypeId: financeApiId })
            break
          case bookingAssignmentsProceedingTypes.PARTIAL: {
            break
          }
          case bookingAssignmentsProceedingTypes.DISCOUNT: {
            assignments.push({ ...baseData, assignmentType: assignmentTypes.DISCOUNT, assignmentTypeId: financeApiId })
            break
          }
          case bookingAssignmentsProceedingTypes.BANK_CHARGE: {
            assignments.push({ ...baseData, assignmentType: assignmentTypes.FEE, assignmentTypeId: financeApiId })
            break
          }
          case bookingAssignmentsProceedingTypes.BOOK_LATER: {
            break
          }
          default:
            break
        }
      }

      newBooking = { ...booking, assignments }
    }

    if (onAssign) {
      return onAssign({
        ...newBooking,
        bookingType: RECEIPT_PAYMENT_TYPES.ASSIGNMENT,
      })
    }

    request(addAssignmentsUrl(newBooking.id), { auth: true, method: 'POST', data: { default: newBooking.assignments } })
      .then(() => {
        toast.showToast({ message: lang.bankingAssignmentsSuccessMessage })
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.bankingAssignmentsErrorMessage })
      })
      .done(() => {
        onConfirm?.() || onClose?.()
        navigateToCurrentPage(navigate, location)
      })
  }

  const continueAssignment = () => {
    const outstandingAmount = getOutstandingAmount()
    const parsedOutstandingAmount = outstandingAmount * -1

    const parsedAssignedAmount = new Decimal(assignedAmount)

    if (selectedBookings.length > 1 && !parsedAssignedAmount.eq(parsedOutstandingAmount)) {
      return toast.showToast({ type: 'error', message: lang.bookingAssignmentMultiSelectionError })
    }

    if (parsedAssignedAmount.eq(parsedOutstandingAmount) || selectedProceeding) {
      return finishAssignment()
    }
  }

  const getOutstandingAmount = () => {
    const { bookings, totalGross } = transaction
    let result = { outstandingAmount: 0 }

    // filter already added bookings for calculating the correct outstanding amount
    const filteredBookings = bookings.filter((booking) => {
      return !selectedBookings
        .map(selectedBooking => selectedBooking.id)
        .includes(booking.id)
    })

    switch (type) {
      case TRANSACTION_TYPE_EXPENSE:
      case TRANSACTION_TYPE_CREDIT_NOTE:
      case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE:
      case TRANSACTION_TYPE_REVENUE:
        result = getStateAndOutstandingAmountReceipt(filteredBookings, totalGross)
        break
      default:
        result = getStateAndOutstandingAmount(filteredBookings, totalGross)
        break
    }
    return result.outstandingAmount
  }

  const outstandingAmount = getOutstandingAmount()

  const difference = new Decimal(outstandingAmount)
    .minus(assignedAmount)
    .toDP(2)
    .toNumber()

  const getOptions = (difference, amount) => {
    if ((difference > 0 && amount < 0) || (difference < 0 && amount >= 0)) {
      return [
        { label: lang.booking.assignDocument.proceedingOptions[SURCHARGE], value: SURCHARGE },
        { label: lang.booking.assignDocument.proceedingOptions[BOOK_LATER], value: BOOK_LATER },
      ]
    } else {
      return [
        { label: lang.booking.assignDocument.proceedingOptions[PARTIAL], value: PARTIAL },
        { label: lang.booking.assignDocument.proceedingOptions[DISCOUNT], value: DISCOUNT },
        { label: lang.booking.assignDocument.proceedingOptions[BANK_CHARGE], value: BANK_CHARGE },
      ]
    }
  }

  const showBalanceFooter = selectedBookings.length === 1 && !!difference

  const options = getOptions(difference, assignedAmount)
  if (showBalanceFooter && (!selectedProceeding || !options.map(({ value }) => value).includes(selectedProceeding))) {
    setSelectedProceeding(options[0].value)
  }

  const actions = [
    <CancelButton
      key='cancelButtonAssignmentDrawer'
      type='cancel'
      label={lang.cancelButtonLabel}
      onClick={handleCancelClick} />,
    <ButtonIconSquare
      key='submitButtonAssignmentDrawer'
      customClass={classes.submitButton}
      type='submit'
      icon={CheckIcon}
      label={lang.payments.paymentDrawer.submitButton}
      disabled={selectedBookings.length < 1}
      onClick={continueAssignment} />,
  ]

  const infoComponent = type === TRANSACTION_TYPE_EXPENSE ? <ExpenseInfo transaction={transactionProps} isOverDue={isOverDue} variant='single' /> : type === TRANSACTION_TYPE_REVENUE ? <RevenueInfo transaction={transactionProps} variant='single' /> : <InvoiceInfo transaction={transactionProps} isOverDue={isOverDue} variant='single' type={type} />
  const secondView = (
    <>
      <BaseDrawerPanelHeader showCloseIcon={false} children={getTitle(type)} />
      <div className={classes.secondViewDetail}>
        {infoComponent}
      </div>
    </>
  )

  return (
    <>
      {!openBankingModal && (<BaseDrawer
        withFooterLine
        className={classes.drawerContainer}
        open={openDrawer}
        enableBackdropClick
        onClose={handleClose}
        secondView={secondView}
        classNameSecondView={classes.secondViewWrapper}
        headerChildren='Buchung Zuordnen'
        footerChildren={actions}
        footerChildrenClass={classes.drawerFooter}
      >
        <BookingSelectionList
          bookingData={transactionProps}
          offsetTop={330}
          bookingType={fetchNegativeBookings ? 'negative' : 'positive'}
          bookingStates={['notAssigned']}
          rowSelection='single'
          listSettingName={BOOKING_SELECTION_LIST_TYPE}
          fetchList={fetchList}
          type={type}
          hideTitle
        />
        <div className={classes.footer}>
          <div className={clsx(classes.differenceWrapper, { show: showBalanceFooter })}>
            {showBalanceFooter && <div className={classes.differenceInnerWrapper}>
              <div className={classes.differenceTitle}>
                {lang.booking.assignDocument.differenceOf} <span className={classes.highlight}>{formatCurrency(Math.abs(difference))}</span> {lang.booking.assignDocument.treatAs}
              </div>
              <RadioInput
                options={options}
                value={selectedProceeding}
                onChange={(proceeding) => setSelectedProceeding(proceeding)}
              />
            </div>}
          </div>
        </div>
      </BaseDrawer>)
      }
      <OnlineBankingModal
        open={openBankingModal}
        onConfirm={() => {
          dispatch(openBankAccountAddModal())
          navigate('/banking')
        }}
        onAbort={() => {
          setOpenBankingModal(false)
          handleClose()
        }}
      />
    </>
  )
}

AssignmentDrawer.propTypes = {
  isOverDue: PropTypes.bool,
  open: PropTypes.bool,
  transaction: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAssign: PropTypes.func,
}

export default AssignmentDrawer
